import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, Copy, Ban, Check, TerminalSquare } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    client.get("/admin/api-keys").then((r) => setKeys(r.data));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/admin/api-keys", { name });
      setFresh(data);
      setKeys((k) => [{ ...data, key: undefined }, ...k]);
      setName("");
      toast.success("API key created — copy it now, it will not be shown again");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"? Pipelines using it will fail.`)) return;
    await client.delete(`/admin/api-keys/${k.id}`);
    setKeys((l) => l.map((x) => (x.id === k.id ? { ...x, revoked: true } : x)));
    toast.success("Key revoked");
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable — select and copy manually");
    }
  };

  return (
    <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none lg:col-span-2" data-testid="api-keys-card">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-400" />
          <h2 className="font-display text-lg font-semibold">API keys — CI compliance gate</h2>
        </div>
        <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{keys.filter((k) => !k.revoked).length} active</Badge>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <form onSubmit={create} className="flex items-end gap-2" data-testid="api-key-form">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Key name</Label>
              <Input required data-testid="api-key-name" placeholder="github-actions-prod" className="mt-2 h-10 border-slate-200" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <Button type="submit" data-testid="api-key-create" className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800">Generate</Button>
          </form>

          {fresh && (
            <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid="api-key-fresh">
              <p className="font-mono-tight text-[10px] uppercase tracking-widest text-emerald-700">New key "{fresh.name}" — shown once</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-white px-2 py-1.5 font-mono-tight text-xs text-slate-900" data-testid="api-key-value">{fresh.key}</code>
                <Button type="button" variant="outline" size="sm" onClick={() => copy(fresh.key)} className="h-8 border-emerald-300" data-testid="api-key-copy">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          )}

          <div className="mt-5 divide-y divide-slate-100">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between py-3" data-testid={`api-key-${k.name}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    {k.name}
                    {k.revoked && <Badge className="rounded-full bg-rose-50 text-[9px] uppercase tracking-widest text-rose-700">revoked</Badge>}
                  </div>
                  <div className="font-mono-tight text-[11px] text-slate-500">
                    {k.prefix}… · {k.uses || 0} calls · {k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleString()}` : "never used"}
                  </div>
                </div>
                {!k.revoked && (
                  <Button variant="ghost" size="sm" onClick={() => revoke(k)} className="h-8 text-slate-500 hover:text-rose-700" data-testid={`api-key-revoke-${k.name}`}>
                    <Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke
                  </Button>
                )}
              </div>
            ))}
            {!keys.length && <p className="py-3 text-sm text-slate-500">No API key yet. Generate one and store it as a CI secret.</p>}
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-950 p-4 text-slate-100" data-testid="ci-snippet">
          <div className="flex items-center gap-2 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">
            <TerminalSquare className="h-3.5 w-3.5" /> CI configuration
          </div>
          <p className="mt-2 text-xs text-slate-300">Generated projects ship with <code>scripts/toastern-check.py</code> and a pipeline that fails on governance violations. Add these secrets to your CI:</p>
          <pre className="mt-3 overflow-x-auto rounded bg-black/40 p-3 font-mono-tight text-[11px] leading-relaxed text-emerald-200">{`TOASTERN_URL=${API_URL}\nTOASTERN_API_KEY=<key generated here>`}</pre>
          <p className="mt-3 text-xs text-slate-300">Manual call:</p>
          <pre className="mt-2 overflow-x-auto rounded bg-black/40 p-3 font-mono-tight text-[11px] leading-relaxed text-slate-200">{`curl -X POST ${API_URL}/api/compliance/check \\\n  -H "X-Toastern-Key: $TOASTERN_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"files":[{"path":"main.tf","content":"..."}]}'`}</pre>
        </div>
      </div>
    </Card>
  );
}
